// Hand-written mirror of backend/app/chat/schemas.py: the chat routers only register with
// CHAT_ENABLED, which generate_openapi.py doesn't set, so `yarn generate-api` never emits them.

export type ChatQuestionType =
  | 'concept_explanation'
  | 'revenue_vs_profitability'
  | 'competitor_comparison'
  | 'performance_change'
  | 'analyst_sentiment'
  | 'news'
  | 'other'
  | 'off_topic';

export interface ChatSource {
  id: string;
  provider: string;
  isins: string[];
  as_of: string | null;
  used: boolean;
}

export interface ChatUsage {
  provider: string;
  model: string;
  classifier_model: string;
  calls: number;
  input_tokens: number;
  cached_input_tokens: number;
  output_tokens: number;
  cost_usd: number;
}

export interface ChatResponse {
  isin: string;
  name: string | null;
  question: string;
  question_type: ChatQuestionType;
  answer: string;
  isins_used: string[];
  unresolved_companies: string[];
  unanswerable_parts: string[];
  follow_up_questions: string[];
  sources: ChatSource[];
  usage: ChatUsage;
}

// 'unavailable': a 404, the route isn't registered (CHAT_ENABLED off, e.g. prod).
export type ChatErrorKind = 'unavailable' | 'unauthorized' | 'invalid' | 'timeout' | 'failed';

export class ChatRequestError extends Error {
  constructor(
    readonly kind: ChatErrorKind,
    readonly status?: number,
  ) {
    super(`Chat request failed: ${kind}${status ? ` (${status})` : ''}`);
  }
}

export const CHAT_REQUEST_TIMEOUT_MS = 90_000;

interface ChatAuth {
  authBridge: { refreshToken(): Promise<unknown> };
  authStore: { token(): string | null };
}

// One retry after a 401 token refresh, none on 5xx (unlike fetchApiData): each attempt is two LLM calls.
export async function askAssetQuestion(
  isin: string,
  question: string,
  { authBridge, authStore }: ChatAuth,
): Promise<ChatResponse> {
  for (let attempt = 0; ; attempt++) {
    const sent = await send(isin, question, authStore.token() ?? '');
    if (sent.ok) return sent.body;
    if (sent.status === 401 && attempt === 0) {
      await authBridge.refreshToken();
      continue;
    }
    throw new ChatRequestError(kindFor(sent.status), sent.status);
  }
}

type Sent = { ok: true; body: ChatResponse } | { ok: false; status: number };

// The timeout spans headers and body: a stalled body would otherwise keep the question pending forever.
async function send(isin: string, question: string, token: string): Promise<Sent> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), CHAT_REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(`/api/v2/assets/${encodeURIComponent(isin)}/chat`, {
      method: 'POST',
      headers: { Authorization: token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ question, language: 'de' }),
      signal: controller.signal,
    });
    if (!response.ok) return { ok: false, status: response.status };
    return { ok: true, body: (await response.json()) as ChatResponse };
  } catch {
    throw new ChatRequestError(controller.signal.aborted ? 'timeout' : 'failed');
  } finally {
    clearTimeout(timeout);
  }
}

function kindFor(status: number): ChatErrorKind {
  if (status === 404) return 'unavailable';
  if (status === 401 || status === 403) return 'unauthorized';
  if (status === 400 || status === 422) return 'invalid';
  return 'failed';
}
